import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ArrowRight,
    AtSign,
    Building2,
    Camera,
    CheckCircle2,
    ChevronDown,
    CreditCard,
    FileCheck2,
    Handshake,
    Loader2,
    MapPin,
    Phone,
    Rocket,
    User,
    X,
} from 'lucide-react';
import { authAPI } from '../services/api';
import LocationSelector from '@/components/LocationSelector';

/**
 * درخواست نمایندگی.
 *
 * فقط چیزهایی اجباری‌اند که بدون‌شان بررسی درخواست ممکن نیست: نام، موبایل و
 * محل فعالیت (ادمین نماینده‌ها را بر اساس استان پیدا می‌کند). ایمیل، شبا،
 * آدرس و عکس اختیاری‌اند — دیتابیس هم هیچ‌کدام را اجباری نمی‌کند و قبلاً فقط
 * فرم بی‌دلیل شبا را الزامی کرده بود، در حالی که شبا تا قبل از اولین تسویه
 * لازم نیست.
 */

const toLatinDigits = (v: string) =>
    v.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

const normalizePhone = (raw: string) => {
    let s = toLatinDigits(raw).replace(/\D/g, '');
    if (s.startsWith('98') && s.length === 12) s = `0${s.slice(2)}`;
    if (s.length === 10 && s.startsWith('9')) s = `0${s}`;
    return s;
};

/** شبا: IR + ۲۴ رقم. کاربر معمولاً با فاصله یا بدون IR می‌چسباند. */
const normalizeIban = (raw: string) => {
    const s = toLatinDigits(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!s) return '';
    return s.startsWith('IR') ? s : `IR${s}`;
};

const STEPS = [
    { icon: FileCheck2, title: 'ثبت درخواست', text: 'همین فرم؛ کمتر از دو دقیقه' },
    { icon: Handshake, title: 'بررسی و تماس', text: 'کارشناس ما با شما هماهنگ می‌کند' },
    { icon: Rocket, title: 'فعال‌سازی پنل', text: 'ورود با همان شماره موبایل' },
];

/* ------------------------------------------------------------- اجزای فرم */

const Section: React.FC<{ n: string; title: string; hint?: string; children: React.ReactNode }> = ({
    n,
    title,
    hint,
    children,
}) => (
    <section className="rounded-3xl border border-stone-200/80 bg-white p-5 sm:p-6">
        <header className="mb-5 flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#06281F] text-sm font-bold text-emerald-300">
                {n}
            </span>
            <div>
                <h2 className="font-bold text-[#0B1F19]">{title}</h2>
                {hint && <p className="text-xs text-stone-500">{hint}</p>}
            </div>
        </header>
        {children}
    </section>
);

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
    icon?: React.ReactNode;
    optional?: boolean;
    error?: string;
    hint?: string;
}

const Field: React.FC<FieldProps> = ({ label, icon, optional, error, hint, className = '', ...props }) => (
    <label className="block">
        <span className="mb-2 flex items-center justify-between text-sm font-medium text-[#0B1F19]">
            {label}
            {optional && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] text-stone-500">اختیاری</span>}
        </span>
        <span className="relative block">
            {icon && <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-stone-400">{icon}</span>}
            <input
                {...props}
                className={`h-12 w-full rounded-2xl border bg-[#FBFAF7] px-4 text-[15px] text-[#0B1F19] outline-none transition placeholder:text-stone-300 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10 ${
                    icon ? 'pr-11' : ''
                } ${error ? 'border-red-400' : 'border-stone-200'} ${className}`}
            />
        </span>
        {error ? (
            <span className="mt-1.5 block text-xs text-red-600">{error}</span>
        ) : hint ? (
            <span className="mt-1.5 block text-xs text-stone-400">{hint}</span>
        ) : null}
    </label>
);

/* ------------------------------------------------------------------ صفحه */

const RequestAgency: React.FC = () => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [iban, setIban] = useState('');
    const [address, setAddress] = useState('');

    const [countryId, setCountryId] = useState('IR');
    const [provinceId, setProvinceId] = useState('');
    const [cityId, setCityId] = useState('');

    const [avatar, setAvatar] = useState<File | null>(null);
    const [showOptional, setShowOptional] = useState(false);
    const [touched, setTouched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const fileRef = useRef<HTMLInputElement>(null);
    const avatarUrl = useMemo(() => (avatar ? URL.createObjectURL(avatar) : null), [avatar]);

    const phoneNorm = normalizePhone(phone);
    const ibanNorm = normalizeIban(iban);

    const errors = {
        firstName: firstName.trim().length < 2 ? 'نام را وارد کنید' : '',
        lastName: lastName.trim().length < 2 ? 'نام خانوادگی را وارد کنید' : '',
        phone: !/^09\d{9}$/.test(phoneNorm) ? 'شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود' : '',
        location: !provinceId || !cityId ? 'استان و شهر را انتخاب کنید' : '',
        email: email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'ایمیل معتبر نیست' : '',
        iban: ibanNorm && !/^IR\d{24}$/.test(ibanNorm) ? 'شبا باید IR و ۲۴ رقم باشد' : '',
    };
    const show = (key: keyof typeof errors) => (touched ? errors[key] : '');
    const hasErrors = Object.values(errors).some(Boolean);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        setError('');

        if (hasErrors) {
            // خطای فیلد اختیاری وقتی بخش بسته است دیده نمی‌شود
            if (errors.email || errors.iban) setShowOptional(true);
            return;
        }

        setLoading(true);
        try {
            const data = new FormData();
            data.append('first_name', firstName.trim());
            data.append('last_name', lastName.trim());
            data.append('phone_number', phoneNorm);
            data.append('country_id', countryId);
            data.append('province_id', provinceId);
            data.append('city_id', cityId);

            // فیلد اختیاری خالی ارسال نمی‌شود تا به‌جای NULL رشته‌ی خالی ذخیره نشود
            if (email.trim()) data.append('email', email.trim());
            if (ibanNorm) data.append('iban', ibanNorm);
            if (address.trim()) data.append('address', address.trim());
            if (avatar) data.append('avatar', avatar);

            await authAPI.requestAgency(data);
            setDone(true);
        } catch (err: any) {
            // interceptor پیام سرور را در err.message می‌گذارد
            setError(err?.message || 'ثبت درخواست ناموفق بود. دوباره تلاش کنید.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div dir="rtl" className="min-h-screen bg-[#F7F5F0]">
            {/* ---------------------------------------------------------- سربرگ */}
            <div className="relative overflow-hidden bg-[#06281F] text-white">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.16]"
                    style={{
                        backgroundImage: 'radial-gradient(rgba(255,255,255,.55) 1px, transparent 1px)',
                        backgroundSize: '22px 22px',
                    }}
                />
                <div aria-hidden className="absolute -left-20 -top-28 h-80 w-80 rounded-full bg-emerald-400/25 blur-3xl" />

                <div className="relative mx-auto max-w-6xl px-5 pb-24 pt-8 sm:px-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <img src="/logo.png" alt="" className="h-10 w-10 rounded-xl bg-white/95 object-contain p-1" />
                            <span className="font-bold">لایسنس اپ</span>
                        </div>
                        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white">
                            <ArrowRight className="h-4 w-4" />
                            ورود نمایندگان
                        </Link>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-12 max-w-2xl"
                    >
                        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300">همکاری با لایسنس اپ</p>
                        <h1 className="mt-3 text-3xl font-extrabold leading-[1.5] sm:text-4xl">
                            نماینده‌ی رسمی شوید و
                            <span className="text-emerald-300"> از هر فروش سهم بگیرید.</span>
                        </h1>
                    </motion.div>

                    <ol className="mt-10 grid gap-3 sm:grid-cols-3">
                        {STEPS.map(({ icon: Icon, title, text }, i) => (
                            <motion.li
                                key={title}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + i * 0.1 }}
                                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm"
                            >
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-400/15">
                                    <Icon className="h-5 w-5 text-emerald-300" />
                                </span>
                                <span>
                                    <span className="block text-sm font-semibold">{title}</span>
                                    <span className="block text-xs text-white/60">{text}</span>
                                </span>
                            </motion.li>
                        ))}
                    </ol>
                </div>
            </div>

            {/* ------------------------------------------------------------ فرم */}
            <div className="relative mx-auto -mt-14 max-w-3xl px-4 pb-16 sm:px-6">
                <AnimatePresence mode="wait">
                    {done ? (
                        <motion.div
                            key="done"
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-xl sm:p-12"
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.1 }}
                                className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50"
                            >
                                <CheckCircle2 className="h-11 w-11 text-emerald-600" />
                            </motion.div>
                            <h2 className="mt-6 text-2xl font-extrabold text-[#0B1F19]">درخواستتان ثبت شد</h2>
                            <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-stone-600">
                                پیامک تأیید برای <span dir="ltr" className="font-semibold">{phoneNorm}</span> ارسال شد.
                                کارشناسان ما درخواست را بررسی می‌کنند و برای ادامه‌ی کار با شما تماس می‌گیرند.
                            </p>
                            <Link
                                to="/login"
                                className="mt-8 inline-flex h-12 items-center justify-center rounded-2xl bg-[#06281F] px-8 font-semibold text-white hover:bg-[#0A3A2D]"
                            >
                                بازگشت به صفحه‌ی ورود
                            </Link>
                        </motion.div>
                    ) : (
                        <motion.form
                            key="form"
                            onSubmit={handleSubmit}
                            noValidate
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                            className="space-y-4"
                        >
                            {/* ۱ — هویت */}
                            <Section n="۱" title="اطلاعات شما">
                                <div className="flex flex-col gap-5 sm:flex-row">
                                    {/* عکس */}
                                    <div className="flex shrink-0 flex-col items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            className="group relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-3xl border-2 border-dashed border-stone-300 bg-[#FBFAF7] transition hover:border-emerald-500"
                                            aria-label="انتخاب تصویر پروفایل"
                                        >
                                            {avatarUrl ? (
                                                <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <Camera className="h-7 w-7 text-stone-400 transition group-hover:text-emerald-600" />
                                            )}
                                        </button>
                                        {avatar ? (
                                            <button
                                                type="button"
                                                onClick={() => setAvatar(null)}
                                                className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-red-600"
                                            >
                                                <X className="h-3 w-3" /> حذف عکس
                                            </button>
                                        ) : (
                                            <span className="text-[11px] text-stone-400">عکس (اختیاری)</span>
                                        )}
                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => e.target.files?.[0] && setAvatar(e.target.files[0])}
                                        />
                                    </div>

                                    <div className="grid flex-1 gap-4 sm:grid-cols-2">
                                        <Field
                                            label="نام"
                                            icon={<User className="h-4 w-4" />}
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            error={show('firstName')}
                                            autoComplete="given-name"
                                        />
                                        <Field
                                            label="نام خانوادگی"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            error={show('lastName')}
                                            autoComplete="family-name"
                                        />
                                        <div className="sm:col-span-2">
                                            <Field
                                                label="شماره موبایل"
                                                icon={<Phone className="h-4 w-4" />}
                                                type="tel"
                                                inputMode="numeric"
                                                dir="ltr"
                                                placeholder="09xx xxx xxxx"
                                                className="text-left tracking-wider"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                error={show('phone')}
                                                hint="ورود بعدی به پنل نماینده با همین شماره است"
                                                autoComplete="tel"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Section>

                            {/* ۲ — محل فعالیت */}
                            <Section n="۲" title="محل فعالیت" hint="مشتریان هر منطقه به نماینده‌ی همان منطقه معرفی می‌شوند">
                                <LocationSelector
                                    selectedCountry={countryId}
                                    selectedProvince={provinceId}
                                    selectedCity={cityId}
                                    onCountryChange={setCountryId}
                                    onProvinceChange={setProvinceId}
                                    onCityChange={setCityId}
                                />
                                {show('location') && (
                                    <p className="mt-2 flex items-center gap-1 text-xs text-red-600">
                                        <MapPin className="h-3 w-3" /> {show('location')}
                                    </p>
                                )}
                            </Section>

                            {/* ۳ — اختیاری */}
                            <section className="overflow-hidden rounded-3xl border border-stone-200/80 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setShowOptional((v) => !v)}
                                    className="flex w-full items-center gap-3 p-5 text-right sm:p-6"
                                    aria-expanded={showOptional}
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-100 text-sm font-bold text-stone-500">
                                        ۳
                                    </span>
                                    <span className="flex-1">
                                        <span className="block font-bold text-[#0B1F19]">اطلاعات تکمیلی</span>
                                        <span className="block text-xs text-stone-500">
                                            ایمیل، شماره شبا و آدرس — همه اختیاری؛ بعداً هم از پنل قابل تکمیل است
                                        </span>
                                    </span>
                                    <ChevronDown
                                        className={`h-5 w-5 text-stone-400 transition-transform ${showOptional ? 'rotate-180' : ''}`}
                                    />
                                </button>

                                <AnimatePresence initial={false}>
                                    {showOptional && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25 }}
                                        >
                                            <div className="grid gap-4 border-t border-stone-100 p-5 sm:grid-cols-2 sm:p-6">
                                                <Field
                                                    label="ایمیل"
                                                    optional
                                                    icon={<AtSign className="h-4 w-4" />}
                                                    type="email"
                                                    dir="ltr"
                                                    className="text-left"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    error={show('email')}
                                                    autoComplete="email"
                                                />
                                                <Field
                                                    label="شماره شبا"
                                                    optional
                                                    icon={<CreditCard className="h-4 w-4" />}
                                                    dir="ltr"
                                                    placeholder="IR00 0000 0000 0000 0000 0000 00"
                                                    className="text-left font-mono text-sm tracking-wide"
                                                    value={iban}
                                                    onChange={(e) => setIban(e.target.value)}
                                                    error={show('iban')}
                                                    hint="برای واریز سهم فروش؛ تا اولین تسویه لازم نیست"
                                                />
                                                <div className="sm:col-span-2">
                                                    <label className="block">
                                                        <span className="mb-2 flex items-center justify-between text-sm font-medium text-[#0B1F19]">
                                                            آدرس
                                                            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] text-stone-500">
                                                                اختیاری
                                                            </span>
                                                        </span>
                                                        <span className="relative block">
                                                            <Building2 className="pointer-events-none absolute right-4 top-4 h-4 w-4 text-stone-400" />
                                                            <textarea
                                                                rows={3}
                                                                value={address}
                                                                onChange={(e) => setAddress(e.target.value)}
                                                                className="w-full resize-none rounded-2xl border border-stone-200 bg-[#FBFAF7] px-4 py-3 pr-11 text-[15px] outline-none transition focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10"
                                                            />
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </section>

                            {error && (
                                <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    {error}
                                </div>
                            )}

                            <div className="sticky bottom-4 z-10">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group relative flex h-14 w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-[#06281F] font-bold text-white shadow-xl shadow-emerald-950/20 transition hover:bg-[#0A3A2D] disabled:opacity-60"
                                >
                                    <span
                                        aria-hidden
                                        className="absolute inset-0 -translate-x-full bg-gradient-to-l from-transparent via-emerald-400/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                                    />
                                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                    <span className="relative">{loading ? 'در حال ثبت…' : 'ثبت درخواست نمایندگی'}</span>
                                </button>
                            </div>
                        </motion.form>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default RequestAgency;
